import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where as whereFn,
  type Firestore,
  type QueryConstraint
} from 'firebase/firestore';
import { signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { db, auth, googleProvider } from './firebase';
import { ADMIN_EMAILS, type SiteSettingsDoc } from './data-model';
import { toast } from '../components/ui';
import { activeDateKeys, mergeDaySeconds, readDaySeconds, startStudyTracker, subscribeDaySeconds } from './study';

/**
 * Google sign-in uses the popup flow. Production must serve
 * Cross-Origin-Opener-Policy: same-origin-allow-popups (see netlify.toml and
 * public/_headers). `same-origin` severs the popup reference on navigation
 * to Google, causing a false auth/popup-closed-by-user error.
 */

// ---------- auth ----------

export type AuthState = {
  loading: boolean;
  user: { uid: string; email: string | null; name: string | null; photoURL: string | null } | null;
  isSignedIn: boolean;
  isAdmin: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
};

export function useAuth(): AuthState {
  const [user, setUser] = useState<AuthState['user']>(null);
  const [loading, setLoading] = useState(true);

  useEffect(
    () =>
      onAuthStateChanged(auth, u => {
        setUser(u ? { uid: u.uid, email: u.email, name: u.displayName, photoURL: u.photoURL } : null);
        setLoading(false);
      }),
    []
  );

  const isAdmin = useMemo(
    () => !!user?.email && (ADMIN_EMAILS as readonly string[]).includes(user.email),
    [user]
  );

  const signInWithGoogle = useCallback(async () => {
    try {
      const res = await signInWithPopup(auth, googleProvider);
      toast(`Signed in as ${res.user.displayName ?? res.user.email}`, 'ok');
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      toast(`Google sign-in failed: ${m}`, 'err');
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  return { loading, user, isSignedIn: !!user, isAdmin, signInWithGoogle, logout };
}

// ---------- generic collections / docs ----------

export type ColState<T> = { data: T[]; loading: boolean; error: string | null };

/** Live query over a collection ordered by `order` (+ optional equality filter). */
export function useOrderedCol<T = Record<string, unknown>>(
  name: string,
  opts: { where?: readonly [string, unknown]; order?: 'ASCENDING' | 'DESCENDING'; enabled?: boolean } = {}
): ColState<T & { id: string }> {
  const [state, setState] = useState<ColState<T & { id: string }>>({ data: [], loading: true, error: null });
  const { where, enabled = true } = opts;
  const whereKey = where ? JSON.stringify(where) : '';

  useEffect(() => {
    if (!enabled) {
      setState(s => ({ ...s, loading: false }));
      return;
    }
    const constraints: QueryConstraint[] = [];
    if (where) constraints.push(whereFn(where[0], '==', where[1]));
    constraints.push(orderBy('order'));
    const q = query(collection(db, name), ...constraints);
    setState(s => ({ ...s, loading: true, error: null }));
    const unsub = onSnapshot(
      q,
      snap => {
        setState({
          data: snap.docs.map(d => ({ ...(d.data() as object), id: d.id }) as T & { id: string }),
          loading: false,
          error: null
        });
      },
      err => setState({ data: [], loading: false, error: err.message })
    );
    return unsub;
  }, [name, whereKey, enabled]);

  return state;
}

/** Live doc by id. */
export function useDoc<T>(name: string, id: string, enabled = true) {
  const [state, setState] = useState({ data: null as (T & { id: string }) | null, loading: true, error: null as string | null });
  useEffect(() => {
    if (!enabled || !id) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState(s => ({ ...s, loading: true }));
    const unsub = onSnapshot(
      doc(db, name, id),
      snap =>
        setState({
          data: snap.exists() ? ({ ...(snap.data() as object), id: snap.id } as T & { id: string }) : null,
          loading: false,
          error: null
        }),
      err => setState({ data: null, loading: false, error: err.message })
    );
    return unsub;
  }, [name, id, enabled]);
  return state;
}

export function useSiteSettings() {
  return useDoc<SiteSettingsDoc>('siteSettings', 'global');
}

/** One-shot fetch (seed / smoke scripts, prefetch). */
export async function fetchAll<T>(dbRef: Firestore, name: string): Promise<Array<T & { id: string }>> {
  const snap = await getDocs(query(collection(dbRef, name), limit(1000), orderBy('order')));
  return snap.docs.map(d => ({ ...(d.data() as object), id: d.id }) as T & { id: string });
}

export async function fetchDocSafe<T>(dbRef: Firestore, path: string): Promise<(T & { id: string }) | null> {
  const snap = await getDoc(doc(dbRef, path));
  return snap.exists() ? ({ ...(snap.data() as object), id: snap.id } as T & { id: string }) : null;
}

// ---------- progress (synced per account, localStorage fallback) ----------

const LS_DONE_KEY = 'hub.done.v1';
const LS_WATCHED_KEY = 'hub.watched.v1';

export type ProgressState = {
  done: Set<string>;
  watched: Set<string>;
  toggleDone: (id: string) => void;
  toggleDoneMany: (ids: string[], complete: boolean) => void;
  markWatched: (id: string) => void;
  xp: number;
  streak: number;
  activeDays: string[];
  /** bumps whenever study seconds tick; use in memos that read the day map */
  minutesRev: number;
};

function readLocal(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(x => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function useProgress(isSignedIn: boolean): ProgressState {
  const [doneArr, setDoneArr] = useState<string[]>(() => readLocal(LS_DONE_KEY));
  const [watchedArr, setWatchedArr] = useState<string[]>(() => readLocal(LS_WATCHED_KEY));
  const doneRef = useRef(doneArr);
  const watchedRef = useRef(watchedArr);
  doneRef.current = doneArr;
  watchedRef.current = watchedArr;
  const writeTimer = useRef<number | undefined>(undefined);
  const [minRev, setMinRev] = useState(0);

  // study-time heartbeat while any component is mounted with this hook (Layout always does)
  useEffect(() => {
    const stop = startStudyTracker();
    const unsub = subscribeDaySeconds(() => setMinRev(r => r + 1));
    return () => {
      stop();
      unsub();
    };
  }, []);

  // hydrate once when signed in (owner write, merge)
  const hydrated = useRef(false);
  useEffect(() => {
    if (!isSignedIn || hydrated.current) return;
    hydrated.current = true;
    const u = auth.currentUser;
    if (!u) return;
    getDoc(doc(db, 'users', u.uid))
      .then(snap => {
        if (snap.exists()) {
          const d = snap.data();
          if (Array.isArray(d.done)) setDoneArr(d.done.filter((x: unknown) => typeof x === 'string'));
          if (Array.isArray(d.watched)) setWatchedArr(d.watched.filter((x: unknown) => typeof x === 'string'));
          const remote = d.activeDays;
          if (remote && typeof remote === 'object' && !Array.isArray(remote)) {
            mergeDaySeconds(remote as Record<string, number>);
          }
        }
      })
      .catch(() => {
        /* keep local */
      });
  }, [isSignedIn]);

  const persist = useCallback(() => {
    const nextDone = doneRef.current;
    const nextWatched = watchedRef.current;
    if (isSignedIn) {
      window.clearTimeout(writeTimer.current);
      writeTimer.current = window.setTimeout(async () => {
        const u = auth.currentUser;
        if (!u) return;
        try {
          // email is required by the rules; include it so first-time doc creation passes
          await setDoc(doc(db, 'users', u.uid), { email: u.email ?? '', done: nextDone, watched: nextWatched, lastActive: Date.now(), activeDays: readDaySeconds() }, { merge: true });
        } catch {
          persistLocal(LS_DONE_KEY, nextDone);
          persistLocal(LS_WATCHED_KEY, nextWatched);
        }
      }, 800);
    } else {
      persistLocal(LS_DONE_KEY, nextDone);
      persistLocal(LS_WATCHED_KEY, nextWatched);
    }
  }, [isSignedIn]);

  const toggleDone = useCallback(
    (id: string) => {
      setDoneArr(prev => {
        doneRef.current = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
        return doneRef.current;
      });
    },
    []
  );

  const toggleDoneMany = useCallback((ids: string[], complete: boolean) => {
    setDoneArr(prev => {
      const set = new Set(prev);
      for (const id of ids) complete ? set.add(id) : set.delete(id);
      doneRef.current = [...set];
      return doneRef.current;
    });
  }, []);

  // schedule persistence whenever state changes (minutes included via minRev)
  useEffect(() => {
    persist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneArr, watchedArr, minRev]);

  const markWatched = useCallback((id: string) => {
    setWatchedArr(prev => {
      if (prev.includes(id)) return prev;
      watchedRef.current = [...prev, id];
      return watchedRef.current;
    });
  }, []);

  const done = useMemo(() => new Set(doneArr), [doneArr]);
  const watched = useMemo(() => new Set(watchedArr), [watchedArr]);
  const activeDays = readActiveDays();
  const streak = computeStreak(activeDays);

  // XP: welcome bonus + completions + watched videos + daily visits + streak milestones
  const xp =
    120 +
    doneArr.length * 30 +
    watchedArr.length * 15 +
    activeDays.length * 20 +
    Math.floor(streak / 7) * 50;

  return { done, watched, toggleDone, toggleDoneMany, markWatched, xp, streak, activeDays, minutesRev: minRev };
}

export function readActiveDays(): string[] {
  // union of the legacy active-day list and days with tracked study seconds
  return [...activeDateKeys()].sort();
}

function persistLocal(key: string, arr: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(arr));
  } catch {
    /* storage unavailable */
  }
}

function computeStreak(days: string[]): number {
  try {
    if (!days.length) return 0;
    let streak = 0;
    const d = new Date();
    for (;;) {
      const key = d.toISOString().slice(0, 10);
      if (days.includes(key)) {
        streak++;
        d.setDate(d.getDate() - 1);
      } else break;
    }
    return streak;
  } catch {
    return 0;
  }
}

export function markActiveToday() {
  try {
    const raw = localStorage.getItem('hub.active.v1');
    const days: string[] = raw ? JSON.parse(raw) : [];
    const today = new Date().toISOString().slice(0, 10);
    if (!days.includes(today)) {
      days.push(today);
      localStorage.setItem('hub.active.v1', JSON.stringify(days.slice(-400)));
    }
  } catch {
    /* ignore */
  }
}
